/**
 * Eclipse toggle — dark / light switch for the header.
 *
 * One orb crosses a small sky rather than swapping a sun glyph for a moon one:
 * travelling left it is a lit disc over a dawn gradient, travelling right a
 * shadow slides across it, carving a crescent over a night sky as the stars
 * come up. One shape, two states, no icon swap — which suits a brand built on
 * Anton and Space Mono more than a pair of stock glyphs would.
 *
 * It is a `role="switch"`, not a button with a label: screen readers then
 * announce the on/off state, and the visible word is decorative.
 */

import React from 'react';
import { useElfTheme } from './elfTheme';

export const ElfModeToggle: React.FC = () => {
  const theme = useElfTheme();
  if (!theme) return null;

  const { mode, setMode } = theme;
  const next = mode === 'dark' ? 'light' : 'dark';

  return (
    <button
      type="button"
      className="elf-eclipse"
      data-mode={mode}
      role="switch"
      aria-checked={mode === 'dark'}
      aria-label={`Switch to ${next} mode`}
      title={`Switch to ${next} mode`}
      onClick={() => setMode(next)}
    >
      <span className="elf-eclipse__track" aria-hidden="true">
        {/* Day sky, cross-faded over the night one — gradients can't tween. */}
        <span className="elf-eclipse__sky" />
        <span className="elf-eclipse__stars" />
        <span className="elf-eclipse__orb" />
      </span>
      {/* Both words are always rendered; the pair slides so the height never
          jumps between the four- and five-letter state. */}
      <span className="elf-eclipse__label" aria-hidden="true">
        <span className="elf-eclipse__word">Light</span>
        <span className="elf-eclipse__word">Dark</span>
      </span>
    </button>
  );
};

export default ElfModeToggle;
